import {
  buildAxiosAuthenticationHeaders,
  buildAxiosAuthenticationHeadersForMultipart,
} from "@src/cloud-connect/api/util/build-axios-authentication-headers";
import { parseMaybeJson } from "@src/cloud-connect/api/util/parse-maybe-json";
import { ragUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { getLanguage } from "@src/cloud-connect/utils/get-language";
import { i18n } from "@src/i18n/i18n";

export interface SourceDocument {
  text: string;
  filename?: string;
  relevance_score: number;
  bm25_score?: number;
  vector_score?: number;
  document_id?: string;
}

export function splitForRendering(
  content: string,
  isStreaming: boolean,
): { thinking: string; answer: string } {
  const openIndex = content.indexOf("<think>");
  const closeIndex = content.indexOf("</think>");

  if (openIndex === -1) {
    return { thinking: "", answer: content };
  }

  if (closeIndex === -1) {
    if (isStreaming) {
      return {
        thinking: content.slice(openIndex + 7),
        answer: "",
      };
    }
    return { thinking: "", answer: content };
  }

  return {
    thinking: content.slice(openIndex + 7, closeIndex).trim(),
    answer: (
      content.slice(0, openIndex) + content.slice(closeIndex + 8)
    ).replace(/^\n+/, ""),
  };
}

export async function* streamAnswer(
  namespace: string,
  question: string,
  sessionId?: string,
  files?: File[],
): AsyncGenerator<
  | { type: "token"; data: string }
  | {
      type: "meta";
      sessionId?: string;
      sources?: SourceDocument[];
      assistantMessageId?: string;
    }
  | { type: "tool-call"; data: unknown }
  | { type: "done" }
  | { type: "error"; data: string }
> {
  const texts = {
    errorStreamAnswer: i18n(
      "modules.aiRagChat.errorStreamAnswer",
      getLanguage(),
    ),
    errorUnexpectedStreamEnd: i18n(
      "modules.aiRagChat.errorUnexpectedStreamEnd",
      getLanguage(),
    ),
    errorNoBodyStreamAnswer: i18n(
      "modules.aiRagChat.errorNoBodyStreamAnswer",
      getLanguage(),
    ),
  };

  const imageFiles = files ?? [];
  const hasFiles = imageFiles.length > 0;
  const url = hasFiles
    ? `${ragUrlEnvVar()}/api/v1/ns/${namespace}/ask-with-images/stream`
    : `${ragUrlEnvVar()}/api/v1/ns/${namespace}/ask/stream`;

  let body: string | FormData;
  let requestHeaders: Record<string, string>;
  if (hasFiles) {
    // FormData bodies must set their own multipart Content-Type — never send a
    // JSON content-type with them.
    requestHeaders = {
      Accept: "text/event-stream",
      ...buildAxiosAuthenticationHeadersForMultipart(),
    };
    const formData = new FormData();
    formData.append("question", question);
    formData.append("use_markdown", "false");
    if (sessionId) formData.append("session_id", sessionId);
    for (const file of imageFiles) {
      formData.append("files", file);
    }
    body = formData;
  } else {
    const payload: any = { question, use_markdown: false };
    if (sessionId) payload.session_id = sessionId;
    body = JSON.stringify(payload);
    requestHeaders = {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...buildAxiosAuthenticationHeaders(),
    };
  }

  const response = await fetch(url, {
    method: "POST",
    headers: requestHeaders,
    body,
  });

  if (!response.ok) {
    yield { type: "error", data: texts.errorStreamAnswer };
    return;
  }

  const reader = response.body?.getReader();
  if (!reader) {
    yield { type: "error", data: texts.errorNoBodyStreamAnswer };
    return;
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: string | null = null;
  let dataLines: string[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      const trimmed = line.replace(/\r$/, "");

      if (trimmed === "") {
        if (dataLines.length > 0) {
          const payload = dataLines.join("\n");

          if (currentEvent === "meta") {
            try {
              const meta: any = JSON.parse(payload);
              yield {
                type: "meta",
                data: payload,
                sessionId: meta.session_id,
                sources: meta.sources,
                assistantMessageId: meta.assistant_message_id,
              } as any;
            } catch {
              yield { type: "meta", data: payload } as any;
            }
          } else if (currentEvent === "token") {
            yield { type: "token", data: payload };
          } else if (
            currentEvent === "tool-call" ||
            currentEvent === "tool_call"
          ) {
            yield { type: "tool-call", data: parseMaybeJson(payload) };
          } else if (currentEvent === "error") {
            yield { type: "error", data: texts.errorStreamAnswer };
          } else if (currentEvent === "done") {
            yield { type: "done", data: payload } as any;
            return;
          }
        }
        currentEvent = null;
        dataLines = [];
        continue;
      }

      if (trimmed.startsWith("event:")) {
        currentEvent = trimmed.slice(6).trim();
        continue;
      }

      if (trimmed.startsWith("data:")) {
        let payload = trimmed.slice(5);
        if (payload.startsWith(" ")) {
          payload = payload.slice(1);
        }
        dataLines.push(payload);
      }
    }
  }

  yield { type: "error", data: texts.errorUnexpectedStreamEnd };
}
